import axios from 'axios';
import { getApiBaseUrl } from './apiConfig';
import { getAccessToken, clearAuth, refreshAccessToken, getRefreshToken, storeAuth } from '../utils/authentication';

const baseURL = getApiBaseUrl();
const shouldDebugApi = process.env.REACT_APP_API_DEBUG === 'true';

const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor: Attach JWT access token automatically
apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If FormData is being sent, remove Content-Type header to let axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    const method = (config.method || 'get').toLowerCase();
    if (method === 'get') {
      config.params = {
        ...(config.params || {}),
        _ts: Date.now(),
      };
    }

    if (shouldDebugApi) {
      // eslint-disable-next-line no-console
      console.debug('[API][request]', method.toUpperCase(), config.url, {
        params: config.params,
      });
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle 401 errors and token refresh
apiClient.interceptors.response.use(
  (response) => {
    if (shouldDebugApi) {
      // eslint-disable-next-line no-console
      console.debug('[API][response]', response.status, response.config?.url, response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      
      if (!refreshToken) {
        // No refresh token, clear auth and redirect to login
        clearAuth();
        processQueue(new Error('No refresh token'), null);
        isRefreshing = false;
        
        // Redirect to login if we're in browser
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const newAuth = await refreshAccessToken(refreshToken);
        
        // Update stored auth
        storeAuth(newAuth);
        
        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAuth.access}`;
        
        // Process queued requests
        processQueue(null, newAuth.access);
        isRefreshing = false;
        
        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Refresh failed, clear auth and redirect to login
        clearAuth();
        processQueue(refreshError, null);
        isRefreshing = false;
        
        // Redirect to login if we're in browser
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

